import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@kizen/kds/Button';
import { useTranslation } from 'react-i18next';
import CustomObjectsService from 'services/CustomObjectsService';
import KizenTypography from 'app/kizentypo';
import useAsyncFnKeepLast from 'hooks/useAsyncFnKeepLast';
import useExportFieldMetadata from 'hooks/useExportFieldMetadata';
import { ContentWidth } from 'components/Layout/PageContentWidth';
import CustomFieldsBuilder, {
  BuilderLoader,
} from 'components/Builders/CustomFieldsBuilder';
import { useFieldEditingModal } from './useFieldEditingModal';
import {
  BigCard,
  BigTitle,
  Header,
  RightHeader,
  StyledPageSizing,
  TitleWrapper,
} from './styles';
import { useSelector } from 'react-redux';
import { getBusinessTeamMembersObject } from 'store/authentication/selectors';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { invalidate } from 'queries/invalidate';

const getTeamMembersModel = (teamMembersObject, t) => ({
  ...teamMembersObject,
  objectType: 'standard',
  entityName: t('Team Member'),
  isCustom: true,
  fetchUrl: 'standard',
  access: {
    view: true,
    edit: false,
    remove: false,
  },
  entityAccess: true,
});

export const TeammembersPage = (props) => {
  const { t } = useTranslation();
  const { field_id } = useParams();
  const [editingFieldId, { onOpenEditFieldOptions, onCloseEditFieldOptions }] =
    useFieldEditingModal(field_id);

  const teamMembersObject = useSelector(getBusinessTeamMembersObject);

  const model = useMemo(
    () => getTeamMembersModel(teamMembersObject, t),
    [teamMembersObject, t]
  );
  const { handleFieldExport } = useExportFieldMetadata({ model });

  const [
    { value: { categorizedFields, fields = EMPTY_ARRAY } = {}, loading },
    refetchFields,
  ] = useAsyncFnKeepLast(async () => {
    return CustomObjectsService.getCategorizedModelFields(teamMembersObject.id);
  }, [teamMembersObject]);

  const fieldToEdit = useMemo(() => {
    if (!editingFieldId || !fields.length) {
      return null;
    }
    return fields.find((f) => f.id === editingFieldId);
  }, [fields, editingFieldId]);

  useEffect(() => {
    refetchFields();
  }, [refetchFields]);

  const updateCategories = useCallback(() => {
    refetchFields();
    invalidate.FIELDS.CUSTOM_OBJECTS('standard', teamMembersObject.id);
  }, [refetchFields, teamMembersObject.id]);

  return (
    <StyledPageSizing {...props}>
      <BigCard as={ContentWidth}>
        <Header>
          <BigTitle as={TitleWrapper}>
            <KizenTypography as="h3" type="subheader">
              {t('{{entityName}} Fields', model)}
            </KizenTypography>
          </BigTitle>
          <RightHeader>
            <Button
              className="pb-[10px]"
              color="secondary"
              onClick={handleFieldExport}
              variant="text"
            >
              {t('Export Field Metadata')}
            </Button>
          </RightHeader>
        </Header>
        {categorizedFields ? (
          <CustomFieldsBuilder
            model={model}
            field={fieldToEdit}
            updateCategories={updateCategories}
            rawCategories={categorizedFields}
            loading={loading}
            onOpenEditFieldOptions={onOpenEditFieldOptions}
            onCloseEditFieldOptions={onCloseEditFieldOptions}
            readOnlyCategories
          />
        ) : (
          <BuilderLoader loading />
        )}
      </BigCard>
    </StyledPageSizing>
  );
};
